// src/main.js
//
// MediaPipe-native pipeline (re-architecture).
//
//   detect → build Matrix4 M from MediaPipe transformation matrix (column-major)
//          → occluder.update(M)        [canonical face mesh posed by M]
//          → glasses.update(M)         [glasses posed by M · A]
//          → render
//
// No anchor, no stabilizer-of-hand-built-basis, no per-face offset/scale/fill/
// bias. Placement and occlusion are consistent by construction because both are
// posed by the same matrix M in the same metric space.
//
// We still smooth M to remove jitter, by decomposing → filtering → recomposing.

import { CATALOG, getSku, loadCatalog } from './core/catalog.js';
import { FaceTracker } from './core/tracker.js';
import { MatrixSmoother } from './core/matrixSmoother.js';
import { PoseSolver, CANONICAL_PD_CM } from './core/poseSolver.js';
import { NoseScaleEstimator, warpedZ } from './core/noseProfile.js';
import { HeadWidthEstimator } from './core/headWidth.js';
import { setHeightFieldNoseScale, setHeightFieldSurface } from './web/canonicalHeightField.js';
import { mountQaBar } from './web/qaBar.js';
import { attachCameraStream, detachCameraStream } from './web/cameraStream.js';
import { Bridge, skuFromMessage } from './sdk/bridge.js';
import { createScene, fitToCanvas, depthScaleFor } from './web/scene.js';
import { loadEnvironment } from './web/envMap.js';
import { Glasses } from './web/glasses.js';
import { Occluder } from './web/occluder.js';
import { LiveOccluder } from './web/liveOccluder.js';
import { HeadTopOccluder } from './web/headTopOccluder.js';
import { ContactShadow } from './web/contactShadow.js';
import { VideoBackground } from './web/videoBackground.js';
import { computeMeasurements } from './web/measurements.js';
import { DebugPanel } from './web/debugPanel.js';
import { buildPicker, setActiveThumb, setStatus, showOverlay, hideOverlay, setFps, showLoader, hideLoader, setPD } from './web/ui.js';
import { Matrix4, Euler, Vector3 } from 'three';

const qs = new URLSearchParams(window.location.search);

// ── Embed / SDK mode ─────────────────────────────────────────────────────────
// When loaded inside an iframe (or with ?embed=1) the app talks to the host page
// over postMessage (see src/sdk/bridge.js) and defers the camera request until
// the host first asks to show the try-on.
const bridge = new Bridge({ allowedOrigin: qs.get('origin') || qs.get('host') || '*' });
const EMBED = bridge.embedded || qs.has('embed');
if (EMBED) document.documentElement.classList.add('embed');

// Whether the user pinned occluder-depth / tracking-speed via URL. When set,
// these take precedence over per-model config (live tuning isn't overwritten on
// model switch).
const _hasOccBiasOverride = Number.isFinite(parseFloat(qs.get('occbias')));
const _hasRespOverride = Number.isFinite(parseFloat(qs.get('resp')));

// Reusable Euler for reading head yaw/pitch from the smoothed matrix (PD gating).
const _euler = new Euler();
const _v = new Vector3();

// Unproject a normalized MediaPipe landmark to world space (cm) at a given depth,
// through the scene camera. Used to find the user's real bridge position.
function unprojectLandmark(lm, camera, depthCm) {
  // Depth: use the depth at which M itself places the canonical bridge, passed in
  // as depthCm (see caller). This keeps the bridge in the SAME depth solve as the
  // glasses (no floating / no sinking), while X,Y come from the real landmark for
  // lateral accuracy at angles.
  const ndcX = lm.x * 2 - 1;
  const ndcY = -(lm.y * 2 - 1);
  _v.set(ndcX, ndcY, 0.5).unproject(camera);
  _v.sub(camera.position).normalize();
  const t = -Math.max(10, Math.abs(depthCm)) / _v.z;
  return new Vector3(
    camera.position.x + _v.x * t,
    camera.position.y + _v.y * t,
    camera.position.z + _v.z * t
  );
}

// Depth (cm, distance in front of camera) at which the matrix M places the
// canonical bridge — the glasses' own depth solve. Used to anchor the live bridge
// so it never floats forward or sinks behind the frame.
const _cb = new Vector3(0, 3.271, 5.236);   // z is re-warped per user (nose scale)
const _cbW = new Vector3();
function bridgeDepthFromMatrix(M) {
  _cbW.copy(_cb).applyMatrix4(M);
  return Math.abs(_cbW.z);
}

// Yields until the browser has painted at least one frame. Used after
// showLoader() so the loader overlay is actually rendered before we start the
// synchronous-heavy GLB load (otherwise a fast/cached load resolves before the
// loader ever appears on screen).
function nextPaint() {
  return new Promise(resolve =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  );
}

const state = {
  videoEl: document.getElementById('camera'),
  canvasEl: document.getElementById('render-canvas'),
  activeSku: CATALOG[0].sku,
  scene: null, camera: null, renderer: null,
  tracker: null,
  smoother: new MatrixSmoother(),
  occluder: null, glasses: null,
  running: false, panel: null,
  booted: false, paused: false, pendingSku: null,
  depthScale: 1,   // MediaPipe(63°) → calibrated-camera depth factor (see scene.js)
  // Own pose solver (roadmap item 1). Refits the canonical face to the 2D
  // landmarks under the calibrated camera. ?solver=mp falls back to MediaPipe's
  // matrix (+ depth rescale).
  // ?solver=hybrid (default): MediaPipe rotation + our depth · pnp: full 6-DoF · mp: MediaPipe only
  solver: (() => { const m = (qs.get('solver') || 'hybrid').toLowerCase();
    return m === 'mp' ? null : new PoseSolver({ rotationFrom: m === 'pnp' ? 'solve' : 'mediapipe' }); })(),
  // True-to-scale (roadmap item 3): head scale from measured PD. ?truescale=on
  truescale: (qs.get('truescale') || 'off').toLowerCase() === 'on',
  _pdScale: 1, _pdSamples: 0,
  // Per-user nose depth (roadmap item 2). ?noseadapt=off disables; ?nosescale=1.2 forces.
  noseAdapt: (qs.get('noseadapt') || 'on').toLowerCase() !== 'off',
  noseEst: new NoseScaleEstimator(),
  noseScale: 1,
  // Per-user head width → temple splay. ?temples=off disables; ?splay=1.1 forces.
  headEst: new HeadWidthEstimator(),
  headWidth: 1,
  // Lag diagnostics (?lagdiag) — ring buffer of per-frame timing + yaw.
  _lag: [], _lagOn: qs.has('lagdiag'), _lm168: { x: 0, y: 0 }, _lm168Vel: { x: 0, y: 0 }, _lastFrameTs: 0,
  occlusionOn: true,
  bridgePin: (qs.get('pin') || 'on').toLowerCase() !== 'off',
  lastMeasurements: null,
  _M: new Matrix4(),
  _frames: 0, _lastFpsTs: 0, _fps: 0, _detMsEMA: 0, _renderMsEMA: 0, _lastMeasTs: 0
};

async function boot() {
  // Show the loader for the ENTIRE cold start. On first load the long waits are
  // the camera, environment map, and especially the face tracker (WASM +
  // landmarker model download) — not the glasses GLB. So the loader must cover
  // the whole init sequence, not just the model load.
  showLoader('Starting…');
  await nextPaint();   // ensure the loader paints before any heavy work

  // Load per-model config (name / noseDistance / scale) before building UI.
  await loadCatalog();
  state.activeSku = CATALOG[0].sku;
  buildPicker({ catalog: CATALOG, activeSku: state.activeSku, onSelect: loadSku });
  mountQaBar({ onPanel: () => state.panel?.toggle(true) });

  if (EMBED) {
    // Tell the host we're alive, then wait until it asks to show the try-on so
    // the camera permission prompt only appears when the user clicks "Try On".
    bridge.post('loaded');
    setStatus('Waiting for host…');
    showLoader('Ready');
    // In QA mode (flag-toggle reloads) don't block on the host: if it hasn't
    // spoken within 1.5 s, continue with the remembered/first SKU.
    const first = qs.has('qa')
      ? await Promise.race([bridge.firstOpen, new Promise(res => setTimeout(() => res({ productId: sessionStorage.getItem('pointai.qa.sku') || '' }), 1500))])
      : await bridge.firstOpen;
    const wanted = skuFromMessage(first) || state.pendingSku;
    if (wanted && CATALOG.some(m => m.sku === wanted)) state.activeSku = wanted;
    try { sessionStorage.setItem('pointai.qa.sku', state.activeSku); } catch (_) {}
    setActiveThumb(state.activeSku);
  }

  setStatus('Requesting camera…');
  showLoader('Requesting camera…');
  try { await attachCameraStream(state.videoEl); }
  catch (err) { hideLoader(); handleCameraError(err); return; }

  const s = createScene({ canvas: state.canvasEl });
  state.scene = s.scene; state.camera = s.camera; state.renderer = s.renderer;

  // Frame-locked video background (default ON). Drawing the detected frame as the
  // background — instead of showing the live <video> — locks the face and glasses
  // to the same instant so the glasses stick to the face during motion. Disable
  // with ?framelock=off (falls back to the live <video> behind the canvas).
  state.frameLock = (qs.get('framelock') || 'on').toLowerCase() !== 'off';
  if (state.frameLock) {
    state.renderer.autoClear = false;
    state.videoBg = new VideoBackground(state.videoEl);
    state.videoEl.style.visibility = 'hidden';   // hide the live feed; bg shows the locked frame
  }
  fitToCanvas(state.renderer, state.camera, state.canvasEl, state.videoEl);

  setStatus('Loading environment…');
  showLoader('Loading environment…');
  await nextPaint();
  const envMap = await loadEnvironment(state.renderer);
  state.scene.environment = envMap;

  // Occluder mode. DEFAULT 'live': the HYBRID per-vertex occluder — real
  // landmark X,Y (traces the user's actual face outline, so it matches the real
  // cheek/jaw at angles) with depth taken from M·canonicalVert (the SAME depth
  // solve as the glasses, so no lens clipping). This fixes the edge gap the
  // canonical average-face occluder leaves at yaw (its cheek/jaw, far from the
  // bridge anchor, swing off the real face). 'canonical' is the average-face
  // fallback. Switch with ?occ=canonical.
  state.occMode = (qs.get('occ') || 'live').toLowerCase();
  state.occluder = (state.occMode === 'canonical') ? new Occluder() : new LiveOccluder();
  state.scene.add(state.occluder.mesh);
  console.log('[occluder] mode:', state.occMode);

  // Front-skull-cap occluder: hides the temple arms over the forehead/front
  // scalp when looking down (the face mesh has no geometry there). Front-half
  // only, so it leaves the ear region free for future temple-tip work. Posed by
  // the same pinned matrix. ?headtop=off disables.
  state.headTopOn = (qs.get('headtop') || 'on').toLowerCase() !== 'off';
  state.headTop = new HeadTopOccluder();
  state.scene.add(state.headTop.mesh);
  if (qs.has('occdebug')) { state.occluder?.setDebugVisible?.(true); state.headTop?.setDebugVisible?.(true); }
  // Forced nose scale for testing (?nosescale=1.2) — applied before the first model loads.
  const forcedNose = parseFloat(qs.get('nosescale'));
  if (Number.isFinite(forcedNose)) applyNoseScale(forcedNose, 'forced');
  const forcedSplay = parseFloat(qs.get('splay'));
  if (Number.isFinite(forcedSplay)) { state.headWidth = forcedSplay; state.headEst.locked = true; }

  const skuItem = getSku(state.activeSku);
  state.glasses = new Glasses({ envMap, lensSpec: skuItem.lens });
  state.glasses.setPinPoint(_cb);
  if (state.headWidth !== 1) state.glasses.setTempleSplay(state.headWidth);
  state.scene.add(state.glasses.root);

  // Contact shadow — grounds the glasses on the face (fixes the perceptual
  // "floating"). Posed by the same pinned matrix as the glasses. ?shadow=off
  // disables; ?shadowop= sets strength.
  state.shadowOn = (qs.get('shadow') || 'on').toLowerCase() !== 'off';
  state.contactShadow = new ContactShadow();
  state.scene.add(state.contactShadow.mesh);
  setStatus('Loading glasses…');
  showLoader(`Loading ${skuItem.name}…`);
  await nextPaint();
  try { await state.glasses.load(skuItem.modelUrl, skuItem.noseDistance, skuItem.scale, skuItem.pantoTilt); }
  catch (err) { console.error(err); setStatus('Model failed'); }
  applyModelConfig(skuItem);

  // The big one on first load: MediaPipe WASM + landmarker model download.
  setStatus('Loading face tracker…');
  showLoader('Loading face tracker…');
  await nextPaint();
  state.tracker = new FaceTracker({ numFaces: 1, useGPU: (qs.get('delegate') || 'gpu').toLowerCase() !== 'cpu' });
  await state.tracker.init();

  window.addEventListener('resize', () => {
    fitToCanvas(state.renderer, state.camera, state.canvasEl, state.videoEl);
    state.depthScale = depthScaleFor(state.camera.fov);
    state.solver?.setCamera(state.camera.fov, state.camera.aspect);
  });

  state.panel = new DebugPanel(makePanelHooks(), {
    // Placement sliders now only do FINE display-time nudges to the FOV and
    // smoothing; the per-face placement knobs are gone. We keep a minimal set.
    noseDistance: state.glasses.getNoseDistance(),
    scaleValue: state.glasses.getScale(),
    pantoTilt: state.glasses.getPantoTilt(),
    responsiveness: state.smoother.getResponsiveness(),
    filterOn: state.smoother.isFilterEnabled(),
    occlusionOn: state.occlusionOn,
    occBias: state.occluder?.getDepthBias?.() ?? 0,
    shadowOn: state.shadowOn,
    headTopOn: state.headTopOn,
    shadowOpacity: state.contactShadow?.getOpacity?.() ?? 0.5,
    lensIntensity: 1.9, frameIntensity: 1.3
  });
  state.panel.toggle(qs.has('panel'));

  setStatus('Tracking', true);
  state.booted = true;
  state.running = true;
  state._needLoaderHide = true;   // hide the loader on the first rendered frame
  requestAnimationFrame(renderLoop);
  // If the host selected another product while we were booting, honour it now.
  if (state.pendingSku && state.pendingSku !== state.activeSku) {
    const sku = state.pendingSku; state.pendingSku = null;
    setActiveThumb(sku);
    loadSku(sku);
  } else {
    bridge.post('selected', selectedPayload(getSku(state.activeSku)));
  }
}

function selectedPayload(item) {
  return { productId: item.sku, sku: item.sku, name: item.name };
}

// ── Host → SDK message handlers ──────────────────────────────────────────────
function requestSku(msg) {
  const sku = skuFromMessage(msg);
  if (!sku) return;
  if (!CATALOG.some(m => m.sku === sku)) {
    bridge.post('error', { reason: 'unknown-sku', message: `No model for product "${sku}"` });
    return;
  }
  if (!state.booted) { state.pendingSku = sku; return; }
  if (sku === state.activeSku) { bridge.post('selected', selectedPayload(getSku(sku))); return; }
  setActiveThumb(sku);
  loadSku(sku);
}

bridge.on('tryon', (msg) => { requestSku(msg); resume(); });
bridge.on('select', (msg) => requestSku(msg));
bridge.on('show', () => resume());
bridge.on('hide', () => pause());
bridge.on('ping', () => { if (state.booted) bridge.post('ready'); else bridge.post('loaded'); });

// Pause when the host moves the panel out of view: stop the render loop and
// release the camera (turns the webcam light off). resume() reattaches it.
function pause() {
  if (!state.booted || state.paused) return;
  state.paused = true;
  state.running = false;
  detachCameraStream(state.videoEl);
  setStatus('Paused');
}

async function resume() {
  if (!state.booted || !state.paused) return;
  state.paused = false;
  setStatus('Requesting camera…');
  showLoader('Resuming…');
  try { await attachCameraStream(state.videoEl); }
  catch (err) { hideLoader(); handleCameraError(err); return; }
  state._fitted = false;
  state.running = true;
  state._needLoaderHide = true;
  setStatus('Tracking', true);
  requestAnimationFrame(renderLoop);
}

document.getElementById('sdk-close')?.addEventListener('click', () => {
  bridge.post('close');
});

// ── Lag diagnostics (?lagdiag) ───────────────────────────────────────────────
// Records, per frame: detection/render time, raw (MediaPipe) yaw vs the yaw we
// actually render, and the raw bridge landmark. The difference rawYaw−outYaw is
// OUR smoother's lag; lag that remains with ?nofilter is MediaPipe's own
// (VIDEO-mode landmark smoothing + ROI tracking) — compare with ?trackmode=image.
const _eRaw = new Euler(), _mRaw = new Matrix4();
function recordLag(nowMs, result) {
  _mRaw.fromArray(result.mpMatrix || result.matrix);
  _eRaw.setFromRotationMatrix(_mRaw, 'YXZ');
  _euler.setFromRotationMatrix(state.smoother.getMatrix(), 'YXZ');
  const row = {
    t: Math.round(nowMs), det: +state._detMsEMA.toFixed(1), ren: +state._renderMsEMA.toFixed(1),
    fps: +state._fps.toFixed(0),
    yawRaw: +(_eRaw.y * 180 / Math.PI).toFixed(1), yawOut: +(_euler.y * 180 / Math.PI).toFixed(1),
    pitchRaw: +(_eRaw.x * 180 / Math.PI).toFixed(1), pitchOut: +(_euler.x * 180 / Math.PI).toFixed(1),
    bx: +(state._lm168.x * (state.videoEl.videoWidth || 1)).toFixed(0)
  };
  const L = state._lag; L.push(row); if (L.length > 300) L.shift();
  if (!state._lagEl) {
    const el = document.createElement('pre');
    el.id = 'lagdiag';
    el.style.cssText = 'position:absolute;left:14px;bottom:110px;z-index:30;margin:0;padding:8px 10px;font:11px ui-monospace,monospace;color:#fff;background:rgba(0,0,0,.6);border-radius:8px;pointer-events:none;white-space:pre';
    document.getElementById('stage').appendChild(el);
    state._lagEl = el;
  }
  if ((state._frames & 3) === 0) {
    const n = L.length, prev = L[Math.max(0, n - 4)];
    const omega = prev ? Math.abs(row.yawRaw - prev.yawRaw) / Math.max(1, row.t - prev.t) * 1000 : 0;
    state._lagEl.textContent =
      `fps ${row.fps}  detect ${row.det}ms  solve ${(state._solveMsEMA||0).toFixed(1)}ms  render ${row.ren}ms\n` +
      (state.solver ? `solver ${state.solver.rotationFrom === 'mediapipe' ? 'hybrid' : 'pnp'}  rms ${(state.solver.rmsPx*1000).toFixed(1)}e-3  vfov ${state.camera.fov.toFixed(1)}°  scale ${state.solver.getScale().toFixed(3)}\n` : 'solver mediapipe\n') +
      `nose ${state.noseScale.toFixed(2)}${state.noseEst.locked ? '' : ' (' + state.noseEst.samples + '/40)'}  head w ${state.headWidth.toFixed(2)}${state.headEst.locked ? '' : ' (' + state.headEst.samples + '/40)'}  seat +${(state.glasses?.getAutoSeatShift?.() ?? 0).toFixed(2)}cm  lens ${state.glasses?.getLensOpacity?.().toFixed(2)}\n` +
      `res ${state.renderer.domElement.width}×${state.renderer.domElement.height}  mode ${state.tracker.runningMode}\n` +
      `yaw mp ${row.yawRaw.toFixed(1)}°  out ${row.yawOut.toFixed(1)}°   pitch mp ${row.pitchRaw.toFixed(1)}°  out ${row.pitchOut.toFixed(1)}°   ndcY ${(1 - 2 * state._lm168.y).toFixed(2)}\n` +
      `ω ${omega.toFixed(0)}°/s  lead ${state.smoother.getLead()}ms  filter ${state.smoother.isFilterEnabled() ? 'on' : 'off'}`;
  }
}

function forcedNoseScale() { return parseFloat(qs.get('nosescale')); }

// Re-run auto-seat against the PERSON's face (live occluder surface in canonical
// space) instead of the average model, so nothing the occluder draws can sit in
// front of the frame's front piece. ?realseat=off keeps the canonical surface.
function reseatOnRealFace(why) {
  if ((qs.get('realseat') || 'on').toLowerCase() === 'off') return;
  const surf = state.occluder?.canonicalSurface?.(state._diag?.pinnedM);
  if (!surf) return;
  setHeightFieldSurface(surf);
  state.glasses?.reseat?.();
  console.log(`[seat] re-seated on the real face (${why}) → +${state.glasses.getAutoSeatShift().toFixed(2)} cm`);
}

// Push a nose scale r into everything that uses the canonical surface.
function applyNoseScale(r, why) {
  state.noseScale = r;
  _cb.z = warpedZ(168, r);                         // bridge depth for pinning
  state.glasses?.setPinPoint?.(_cb);
  state.occluder?.setNoseScale?.(r);
  setHeightFieldNoseScale(r);
  state.glasses?.reseat?.();
  console.log(`[nose] scale ${r.toFixed(3)} (${why}) → bridge z ${_cb.z.toFixed(2)} cm, reseated`);
}

function makePanelHooks() {
  return {
    onNoseDistance: (cm) => state.glasses.setNoseDistance(cm),
    onScale: (v) => state.glasses.setScale(v),
    onPantoTilt: (v) => state.glasses.setPantoTilt(v),
    onResponsiveness: (v) => state.smoother.setResponsiveness(v),
    onToggleFilter: (on) => state.smoother.setFilterEnabled(on),
    onToggleOcclusion: (on) => { state.occlusionOn = on; state.occluder.setEnabled(on); },
    onOccluderBias: (cm) => { state.occluder?.setDepthBias?.(cm); },
    onToggleOccluderVisible: (on) => { state.occluder?.setDebugVisible?.(on); state.headTop?.setDebugVisible?.(on); },
    onToggleHeadTop: (on) => { state.headTopOn = on; if (!on) state.headTop?.hide(); },
    onToggleShadow: (on) => { state.shadowOn = on; if (!on) state.contactShadow?.hide(); },
    onShadowOpacity: (v) => { state.contactShadow?.setOpacity?.(v); },
    onGenerateLog: () => captureDiagnostic(),
    onLensIntensity: (v) => state.glasses.setLensIntensity(v),
    onFrameIntensity: (v) => state.glasses.setFrameIntensity(v),
    onReport: () => buildReport()
  };
}

function renderLoop(nowMs) {
  if (!state.running) return;
  requestAnimationFrame(renderLoop);

  // Frame-lock, exact: grab the video frame FIRST, then run detection on that
  // very pixel buffer. The previous order (detect on <video>, then copy the
  // <video>) let the video advance by a frame during the ~15-25 ms detection, so
  // the background could be one frame NEWER than the landmarks — a random
  // 1-frame lag that shows up as jitter/lag on fast turns. ?lockdetect=off
  // restores the old order.
  let src = state.videoEl;
  if (state.videoBg && (qs.get('lockdetect') || 'on') !== 'off' && state.videoBg.captureFrame()) {
    src = state.videoBg.canvas;
  } else if (state.videoBg) {
    // fallback: old order (detect on video, then copy)
  }
  const t0 = performance.now();
  const result = state.tracker.detect(src, nowMs);
  state._detMsEMA = state._detMsEMA * 0.9 + (performance.now() - t0) * 0.1;
  if (src === state.videoEl && state.videoBg) state.videoBg.captureFrame();

  if (result && result.matrix && result.matrix.length === 16) {
    // One-time refit once the video reports its true dimensions, so the camera
    // aspect matches the capture aspect exactly.
    if (!state._fitted && state.videoEl.videoWidth) {
      fitToCanvas(state.renderer, state.camera, state.canvasEl, state.videoEl);
      state.depthScale = depthScaleFor(state.camera.fov);
      state.solver?.setCamera(state.camera.fov, state.camera.aspect);
      state._fitted = true;
    }
    // Camera calibration: MediaPipe solved the pose for a 63° virtual camera.
    // We render with the real camera's FOV, so push the head back along Z by
    // the focal-length ratio. Screen position and size are preserved exactly;
    // only the (previously exaggerated) perspective across the frame changes —
    // this is what fixes the far-side lens shrinking at yaw.
    if (state.depthScale !== 1) result.matrix[14] *= state.depthScale;

    // Own PnP refit under the calibrated camera. MediaPipe's (rescaled) matrix
    // is only the cold-start seed; after that the solver warm-starts itself.
    if (state.solver && state._fitted) {
      const t1 = performance.now();
      const m = state.solver.solve(result.landmarks, result.matrix);
      state._solveMsEMA = (state._solveMsEMA || 0) * 0.9 + (performance.now() - t1) * 0.1;
      result.mpMatrix = result.matrix;
      result.matrix = m;
    }
    // Smooth the raw matrix (decompose→filter→recompose) then use it.
    state.smoother.update(result.matrix, nowMs / 1000);
    state._M.copy(state.smoother.getMatrix());

    // Compute the live bridge (for pinning) once, then pose BOTH the glasses and
    // the canonical occluder with the SAME pinned matrix so they share one depth
    // frame — lens never clips, nose occludes the far lens correctly at yaw.
    let liveBridge = null;
    if (state.bridgePin && result.landmarks && result.landmarks.length > 168) {
      const glassesM0 = state.smoother.getGlassesMatrix();
      const bridgeDepth = bridgeDepthFromMatrix(glassesM0);   // M's own bridge depth
      // Bridge-pinning overrides M's translation with the raw landmark, so the
      // smoother's motion prediction must be applied to the landmark as well:
      // extrapolate lm168 by its (lightly filtered) velocity × lead.
      const lm = result.landmarks[168];
      const leadSec = state.smoother.getLead() / 1000;
      const dtF = Math.min(0.1, Math.max(1e-3, (nowMs - state._lastFrameTs) / 1000));
      if (state._lastFrameTs) {
        state._lm168Vel.x += ((lm.x - state._lm168.x) / dtF - state._lm168Vel.x) * 0.5;
        state._lm168Vel.y += ((lm.y - state._lm168.y) / dtF - state._lm168Vel.y) * 0.5;
      }
      state._lm168.x = lm.x; state._lm168.y = lm.y;
      const lmP = leadSec > 0
        ? { x: lm.x + state._lm168Vel.x * leadSec, y: lm.y + state._lm168Vel.y * leadSec, z: lm.z }
        : lm;
      liveBridge = unprojectLandmark(lmP, state.camera, bridgeDepth);
    }
    state._lastFrameTs = nowMs;
    const glassesM = state.smoother.getGlassesMatrix();
    const pinnedM = state.glasses.computePinnedMatrix(glassesM, liveBridge) || glassesM;

    if (state.occlusionOn) {
      if (state.occMode === 'live') {
        // Hybrid live occluder: real landmark X,Y + depth from M·canonicalVert
        // (the SAME pinnedM the glasses use → one depth frame, no clipping).
        state.occluder.update(result.landmarks, pinnedM, state.camera);
      } else {
        // Canonical occluder posed by the SAME pinned matrix as the glasses.
        state.occluder.update(pinnedM);
      }
    } else {
      state.occluder.hide();
    }

    // Front-skull-cap: hide temple arms over the forehead. Same pinned frame.
    if (state.headTopOn && state.occlusionOn) {
      state.headTop.update(result.landmarks, pinnedM, state.camera);
    } else {
      state.headTop.hide();
    }

    // Pose the glasses with the SAME pinned matrix used for the occluders above,
    // so they all stay in one depth frame. glasses applies A_shifted on top.
    state.glasses.update(pinnedM);

    // Contact shadow tracks with the same pinned matrix (sits on the skin below
    // the frame). Hidden if occlusion is off or shadow disabled.
    if (state.shadowOn && state.occlusionOn) state.contactShadow.update(pinnedM);
    else state.contactShadow.hide();

    // Stash the latest frame data so the diagnostic log button can capture it.
    state._diag = { result, glassesM, pinnedM, liveBridge };

    if (state._lagOn) recordLag(nowMs, result);

    if (nowMs - state._lastMeasTs > 200) {
      state.lastMeasurements = computeMeasurements({
        landmarks: result.landmarks,
        videoWidth: state.videoEl.videoWidth,
        videoHeight: state.videoEl.videoHeight,
        placement: { offset: { z: 0 }, rotation: { x: 0 }, scale: 1 }
      });
      if (state.lastMeasurements) {
        // PD is only reliable when the head is square to the camera — at an
        // angle the iris foreshortens and PD drifts low. Read head yaw/pitch
        // from the smoothed matrix and only show PD when facing forward;
        // otherwise hide the badge.
        _euler.setFromRotationMatrix(state._M, 'YXZ');
        const yawDeg = Math.abs(_euler.y * 180 / Math.PI);
        const pitchDeg = Math.abs(_euler.x * 180 / Math.PI);
        const facingForward = yawDeg < 12 && pitchDeg < 12;
        setPD(facingForward ? state.lastMeasurements.pd : null,
              state.lastMeasurements.confidence);
        // Per-user nose depth: sample on frontal frames, lock, then warp every
        // canonical surface (occluders, seating, bridge depth) once.
        if (state.noseAdapt && facingForward && !state.noseEst.locked && !Number.isFinite(forcedNoseScale())) {
          state.noseEst.push(result.landmarks);
          if (state.noseEst.locked) applyNoseScale(state.noseEst.value, `locked after ${state.noseEst.samples} frames`);
        }
        // Head width → temple splay (frontal frames, locked after 40).
        if (facingForward && !state.headEst.locked && state.solver) {
          state.headEst.push(result.landmarks, result.matrix, state.solver.f, state.solver.aspect);
          if (state.headEst.locked) {
            state.headWidth = state.headEst.value;
            state.glasses.setTempleSplay(state.headWidth);
            console.log(`[head] width ratio ${state.headWidth.toFixed(3)} locked`);
            reseatOnRealFace('head width locked');
          }
        }
        // True-to-scale: the user's head size relative to the canonical model,
        // from the iris-anchored PD. Heavily averaged, then frozen after 40
        // confident frontal samples so the frame size never breathes.
        if (state.truescale && state.solver && facingForward &&
            state.lastMeasurements.confidence > 0.8 && state._pdSamples < 40) {
          const sPD = (state.lastMeasurements.pd / 10) / CANONICAL_PD_CM;
          state._pdScale += (sPD - state._pdScale) * (state._pdSamples === 0 ? 1 : 0.1);
          state._pdSamples++;
          state.solver.setScale(state._pdScale);
          if (state._pdSamples === 1 || state._pdSamples === 40) state.glasses.setHeadScale(state.solver.getScale());
          if (state._pdSamples === 40) console.log(`[truescale] locked head scale ${state._pdScale.toFixed(3)} (PD ${(state._pdScale * CANONICAL_PD_CM * 10).toFixed(1)} mm)`);
        }
      }
      state._lastMeasTs = nowMs;
    }
  }

  const r0 = performance.now();
  if (state.videoBg) {
    // Draw the frame-locked video background first, then the 3D scene on top
    // without clearing it. autoClear is false (set at init) so the background
    // survives the second render.
    state.renderer.clear();
    state.videoBg.render(state.renderer);
    state.renderer.render(state.scene, state.camera);
  } else {
    state.renderer.render(state.scene, state.camera);
  }
  state._renderMsEMA = state._renderMsEMA * 0.9 + (performance.now() - r0) * 0.1;

  // Everything is initialised and the first frame is on screen — hide the
  // cold-start loader now (covers camera + env + glasses + tracker WASM).
  if (state._needLoaderHide) {
    state._needLoaderHide = false;
    hideLoader();
    bridge.post('ready');
  }

  state._frames++;
  if (nowMs - state._lastFpsTs > 500) {
    state._fps = (state._frames * 1000) / (nowMs - state._lastFpsTs);
    setFps(state._fps);
    state._frames = 0; state._lastFpsTs = nowMs;
  }
}

async function loadSku(sku) {
  const item = getSku(sku);
  if (!item) return;
  state.activeSku = sku;
  try { sessionStorage.setItem('pointai.qa.sku', sku); } catch (_) {}
  setStatus(`Loading ${item.name}…`);
  showLoader(`Loading ${item.name}…`);
  await nextPaint();   // ensure the loader is painted before loading starts
  try {
    await state.glasses.load(item.modelUrl, item.noseDistance, item.scale, item.pantoTilt);
    applyModelConfig(item);
    state.panel?.setNoseDistance(state.glasses.getNoseDistance());
    state.panel?.setScale(state.glasses.getScale());
    state.panel?.setPantoTilt(state.glasses.getPantoTilt());
    state.panel?.setResponsiveness?.(state.smoother.getResponsiveness());
    state.panel?.setOccluderBias?.(state.occluder?.getDepthBias?.() ?? 0);
    setStatus('Tracking', true);
    bridge.post('selected', selectedPayload(item));
  }
  catch (err) { console.error(err); setStatus(`Failed: ${item.name}`); }
  finally { hideLoader(); }
}

// Apply a model's per-model config values that live outside the Glasses object:
// occluder depth (occBias) and tracking responsiveness. URL overrides (?occbias=,
// ?resp=) take precedence and are left untouched so live tuning still works.
function applyModelConfig(item) {
  if (!_hasOccBiasOverride && Number.isFinite(item.occBias)) {
    state.occluder?.setDepthBias?.(item.occBias);
  }
  if (!_hasRespOverride && Number.isFinite(item.responsiveness)) {
    state.smoother.setResponsiveness(item.responsiveness);
  }
}

// ── Diagnostic log capture ──────────────────────────────────────────────────
// Captures the full geometric state at the instant of the click so we can
// compare placement across head angles objectively. The user clicks at one held
// angle, then another, and the two logs together reveal what's drifting.
let _diagCounter = 0;
function captureDiagnostic(label) {
  const d = state._diag;
  if (!d || !d.result) { console.warn('[diag] no frame data yet'); return; }
  const { result, glassesM, pinnedM, liveBridge } = d;
  const L = [];
  const tag = label || `capture #${++_diagCounter}`;
  L.push(`========== POINTAI VTO DIAGNOSTIC — ${tag} ==========`);
  L.push(`time: ${new Date().toISOString()}`);
  L.push(`active SKU: ${state.activeSku}`);
  L.push('');

  // Head pose from the smoothed matrix.
  _euler.setFromRotationMatrix(state._M, 'YXZ');
  const deg = r => (r * 180 / Math.PI).toFixed(1);
  L.push(`HEAD POSE (deg):  yaw=${deg(_euler.y)}  pitch=${deg(_euler.x)}  roll=${deg(_euler.z)}`);
  L.push(`head distance D (|M.t.z|): ${Math.abs(result.matrix[14]).toFixed(2)} cm`);
  L.push('');

  // Raw + smoothed matrix translation.
  L.push(`raw M translation:    [${result.matrix[12].toFixed(2)}, ${result.matrix[13].toFixed(2)}, ${result.matrix[14].toFixed(2)}]`);
  const gt = state.smoother.getTranslation();
  L.push(`smoothed translation: [${gt.x.toFixed(2)}, ${gt.y.toFixed(2)}, ${gt.z.toFixed(2)}]`);
  L.push('');

  // Glasses placement params.
  L.push(`GLASSES PARAMS: noseDist=${state.glasses.getNoseDistance().toFixed(2)}cm  scale=${state.glasses.getScale().toFixed(2)}  pantoTilt=${state.glasses.getPantoTilt().toFixed(1)}°`);
  L.push(`bridgePin: ${state.bridgePin ? 'ON' : 'off'}   occMode: ${state.occMode}   occBias: ${(state.occluder?.getDepthBias?.() ?? 0).toFixed(2)}cm`);
  L.push('');

  // Bridge analysis: where the REAL bridge landmark is vs where the frame bridge lands.
  if (result.landmarks && result.landmarks.length > 168) {
    const lm = result.landmarks[168];
    L.push(`REAL bridge landmark 168 (normalized): x=${lm.x.toFixed(4)} y=${lm.y.toFixed(4)} z=${lm.z.toFixed(4)}`);
    if (liveBridge) {
      L.push(`unprojected live bridge (world cm): [${liveBridge.x.toFixed(2)}, ${liveBridge.y.toFixed(2)}, ${liveBridge.z.toFixed(2)}]`);
    } else {
      L.push(`unprojected live bridge: (pinning OFF)`);
    }
    // Where the frame's bridge actually lands = pinnedM · (A_shifted · modelBridge)
    const fb = state.glasses.getFrameBridgeWorld ? state.glasses.getFrameBridgeWorld(pinnedM) : null;
    if (fb) L.push(`FRAME bridge lands at (world cm):    [${fb.x.toFixed(2)}, ${fb.y.toFixed(2)}, ${fb.z.toFixed(2)}]`);
    if (fb && liveBridge) {
      const dx = (fb.x - liveBridge.x), dy = (fb.y - liveBridge.y), dz = (fb.z - liveBridge.z);
      L.push(`  → offset frame vs real bridge: [${dx.toFixed(2)}, ${dy.toFixed(2)}, ${dz.toFixed(2)}] cm`);
    }
  }
  L.push('');

  // Key landmark depths to understand face geometry: bridge(168), nose tip(1),
  // eye centers, to see how far the nose projects vs eyes for this user.
  if (result.landmarks) {
    const Lk = result.landmarks;
    const camDepth = (lm) => {
      const wb = unprojectLandmark(lm, state.camera, bridgeDepthFromMatrix(glassesM));
      return wb;
    };
    const pts = { 'bridge168': 168, 'noseTip1': 1, 'noseBottom2': 2, 'leftEye33': 33, 'rightEye263': 263 };
    L.push(`KEY LANDMARKS (normalized x,y,z):`);
    for (const [name, i] of Object.entries(pts)) {
      if (Lk[i]) L.push(`  ${name.padEnd(12)} x=${Lk[i].x.toFixed(4)} y=${Lk[i].y.toFixed(4)} z=${Lk[i].z.toFixed(4)}`);
    }
    // nose projection: z of nose tip vs eye line (more negative z = closer to cam)
    if (Lk[1] && Lk[33] && Lk[263]) {
      const eyeZ = (Lk[33].z + Lk[263].z) / 2;
      L.push(`  nose tip z - eye z = ${(Lk[1].z - eyeZ).toFixed(4)} (how far nose projects toward camera)`);
    }
  }
  L.push('');
  L.push(`pinnedM (col-major): [${Array.from(pinnedM.elements).map(n=>n.toFixed(3)).join(', ')}]`);
  if (state._lag.length) {
    L.push('');
    L.push(`LAG TRACE (last ${state._lag.length} frames) t_ms,detect_ms,render_ms,fps,yaw_raw,yaw_out,bridge_x_px`);
    for (const r of state._lag) L.push(`${r.t},${r.det},${r.ren},${r.fps},${r.yawRaw},${r.yawOut},${r.bx}`);
  }
  L.push(`================================================================`);

  const text = L.join('\n');
  console.log(text);
  // Download as a text file.
  const blob = new Blob([text], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `vto-diag-${Date.now()}.txt`;
  a.click();
  URL.revokeObjectURL(a.href);
  setStatus(`Diagnostic captured (${tag})`, true);
}

function buildReport() {
  const m = state.lastMeasurements;
  const t = state.smoother.getTranslation();
  const L = [];
  L.push('POINTAI GOGGLES VTO — DIAGNOSTIC REPORT (MediaPipe-native)');
  L.push('Generated: ' + new Date().toISOString());
  L.push('SKU: ' + state.activeSku);
  L.push('');
  L.push('── PERFORMANCE ──');
  L.push(`FPS:               ${state._fps.toFixed(1)}`);
  L.push(`Detection time:    ${state._detMsEMA.toFixed(2)} ms / frame`);
  L.push(`Render time:       ${state._renderMsEMA.toFixed(2)} ms / frame`);
  L.push(`Video resolution:  ${state.videoEl.videoWidth} × ${state.videoEl.videoHeight}`);
  L.push(`Camera vFOV:       ${state.camera.fov.toFixed(1)}°  (depth scale ${state.depthScale.toFixed(3)})`);
  L.push(`Auto-seat shift:   ${(state.glasses.getAutoSeatShift?.() ?? 0).toFixed(2)} cm`);
  L.push(`Nose scale:        ${state.noseScale.toFixed(3)} (${state.noseEst.locked ? 'locked' : state.noseEst.samples + ' samples'})`);
  L.push(`Head width ratio:  ${state.headWidth.toFixed(3)} → temple splay ${state.glasses.getTempleSplay?.().toFixed(3)}`);
  L.push(`Pose solver:       ${state.solver ? `pnp (rms ${(state.solver.rmsPx*1000).toFixed(2)}e-3 ndc, ${(state._solveMsEMA||0).toFixed(2)} ms, scale ${state.solver.getScale().toFixed(3)})` : 'mediapipe'}`);
  L.push('');
  L.push('── PLACEMENT (matrix-driven, zero per-face tuning) ──');
  L.push(`Glasses pose:      M · A   (M = MediaPipe matrix, A = baked alignment)`);
  L.push(`Nose distance:     ${state.glasses.getNoseDistance().toFixed(2)} cm`);
  L.push(`Scale:             ${state.glasses.getScale().toFixed(2)}×`);
  L.push(`Occluder pose:     M       (canonical face mesh)`);
  L.push(`Head translation:  x=${t.x.toFixed(2)} y=${t.y.toFixed(2)} z=${t.z.toFixed(2)} cm`);
  L.push(`Responsiveness:    ${(state.smoother.getResponsiveness()*100).toFixed(0)}%`);
  L.push(`Filter:            ${state.smoother.isFilterEnabled()}`);
  L.push(`Occlusion:         ${state.occlusionOn}`);
  L.push('');
  L.push('── EYEWEAR MEASUREMENTS (estimates, monocular RGB) ──');
  if (m) {
    L.push(`PD (total):        ${m.pd} mm`);
    L.push(`Monocular PD L/R:  ${m.monoPD_left} / ${m.monoPD_right} mm`);
    L.push(`Segment height:    ${m.segmentHeight} mm`);
    L.push(`Pantoscopic tilt:  ${m.pantoscopicTilt}°`);
    L.push(`Vertex distance:   ${m.vertexDistance} mm`);
    L.push(`Confidence:        ${(m.confidence*100).toFixed(0)}%`);
  } else L.push('(no measurements yet)');
  return L.join('\n');
}

function handleCameraError(err) {
  const reason = err.message || 'camera-error';
  bridge.post('error', { reason, message: String(err.cause?.message || err.message || err) });
  const cfg = {
    'permission-denied': { title:'Camera access denied', message:'Grant camera permission then reload.', buttonText:'Reload', onClick:()=>location.reload() },
    'no-camera': { title:'No camera found', message:'A front-facing camera is required.', buttonText:'OK', onClick:hideOverlay },
  }[reason] || { title:'Camera unavailable', message:'Another app may be using the camera.', buttonText:'Retry', onClick:()=>location.reload() };
  showOverlay(cfg);
}

boot().catch(err => {
  console.error('[main] boot failed', err);
  bridge.post('error', { reason: 'boot-failed', message: String(err?.message || err) });
  hideLoader();
  showOverlay({ title:'Could not start', message:'Unexpected error. Check console.', buttonText:'Reload', onClick:()=>location.reload() });
});
